import { Badge, type BadgeProps } from "@coval/ui"

export interface TierGaugeTier {
  label: string
  variant: NonNullable<BadgeProps["variant"]>
  blurb: string
}

interface TierGaugeProps {
  value: number
  tier: TierGaugeTier
  /** Gauge scale bounds; values outside are clamped to the edges. */
  min: number
  max: number
  /** Shaded "good" band, in the same units as min/max. */
  band: { low: number; high: number }
  formatValue?: (value: number) => string
  formatTick?: (tick: number) => string
  /** Small unit label beside the headline value (e.g. "st", "LUFS"). */
  unit?: string
  /** Overrides the right-edge tick label (e.g. "6+ st"). */
  maxTickLabel?: string
  /** Trailing muted sentence after the tier blurb (e.g. what the shading means). */
  bandNote?: string
}

/**
 * Compact summary "slider": headline value, tier badge, and a horizontal gauge
 * with a shaded target band and a marker at the value.
 */
export function TierGauge({
  value,
  tier,
  min,
  max,
  band,
  formatValue = (v) => v.toFixed(2),
  formatTick = (t) => `${t}`,
  unit,
  maxTickLabel,
  bandNote,
}: TierGaugeProps) {
  const span = max - min
  const markerPct = Math.min(Math.max((value - min) / span, 0), 1) * 100
  const bandLeftPct = ((band.low - min) / span) * 100
  const bandWidthPct = ((band.high - band.low) / span) * 100

  return (
    <div className="space-y-1">
      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-bold">{formatValue(value)}</span>
        {unit && <span className="text-sm text-coval-text-muted">{unit}</span>}
        <Badge variant={tier.variant} className="ml-1">
          {tier.label}
        </Badge>
      </div>
      <div
        className="relative h-3 rounded-full bg-surface-2"
        role="meter"
        aria-label={`${tier.label} — ${formatValue(value)}${unit ? ` ${unit}` : ""}`}
        aria-valuenow={value}
        aria-valuemin={min}
        aria-valuemax={max}
      >
        <div
          className="absolute top-0 h-full bg-emerald-300/50 dark:bg-emerald-500/30"
          style={{ left: `${bandLeftPct}%`, width: `${bandWidthPct}%` }}
        />
        <div
          className="absolute top-[-2px] h-[calc(100%+4px)] w-0.5 bg-coval-contrast"
          style={{ left: `${markerPct}%` }}
        />
      </div>
      <div className="flex justify-between text-[10px] font-mono text-coval-text-muted">
        <span>{formatTick(min)}</span>
        <span>{formatTick(band.low)}</span>
        <span>{formatTick(band.high)}</span>
        <span>{maxTickLabel ?? formatTick(max)}</span>
      </div>
      <p className="text-xs text-coval-text-muted">
        {tier.blurb}
        {bandNote && <span className="text-coval-text-muted"> {bandNote}</span>}
      </p>
    </div>
  )
}
